import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Alert, Space, Input } from 'antd';
import { SafetyCertificateOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useUserContext } from '../../../Contexts/user';
import { mfaVerifyLogin } from '../../../API/auth';
import logo from '../../../assets/Icons/logo.png';
import './styles.css';

const { Title, Text } = Typography;

function MFAVerify() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setUserData } = useUserContext();
  const navigate = useNavigate();

  // If there's no pending mfa_token, the user shouldn't be here
  useEffect(() => {
    const mfaToken = sessionStorage.getItem('mfa_token');
    if (!mfaToken) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async () => {
    const mfaToken = sessionStorage.getItem('mfa_token');
    if (!mfaToken) {
      navigate('/login');
      return;
    }

    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code from Google Authenticator.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await mfaVerifyLogin({ mfa_token: mfaToken, code });

      if (res?.status !== 200) {
        throw new Error(res?.response?.data?.error || 'Invalid code. Please try again.');
      }

      // Clean up the temporary token
      sessionStorage.removeItem('mfa_token');

      // Complete the login
      setUserData(res.data.user);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    sessionStorage.removeItem('mfa_token');
    navigate('/login');
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
    if (error) setError(null);
  };

  return (
    <div className="mfa-verify-wrapper">
      <Card className="mfa-verify-card">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ textAlign: 'center' }}>
            <img src={logo} alt="Arett Logo" style={{ width: 70, marginBottom: 12 }} />
            <div className="mfa-icon-circle">
              <SafetyCertificateOutlined style={{ fontSize: 28, color: '#1890ff' }} />
            </div>
            <Title level={3} style={{ margin: '12px 0 4px' }}>Two-Factor Authentication</Title>
            <Text type="secondary">
              Open Google Authenticator and enter the 6-digit code for Arett Contract Portal.
            </Text>
          </div>

          {error && (
            <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} />
          )}

          {/* Code Input */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Authentication Code
            </Text>
            <Input
              value={code}
              onChange={handleCodeChange}
              onPressEnter={handleSubmit}
              placeholder="000000"
              maxLength={6}
              size="large"
              className="mfa-code-input"
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
              Code refreshes every 30 seconds.
            </Text>
          </div>

          {/* Actions */}
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Button
              type="primary"
              size="large"
              block
              loading={loading}
              onClick={handleSubmit}
              disabled={code.length !== 6}
            >
              Verify & Sign In
            </Button>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              block
              onClick={handleBack}
              style={{ color: '#8c8c8c' }}
            >
              Back to Login
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
}

export default MFAVerify;
